import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../utils/auth';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Stack,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import HomeIcon from '@mui/icons-material/Home';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import { roommateAPI } from '../../utils/api';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { formatBathrooms } from '../../utils/formatters';

const DEFAULT_PAGE_TITLE = 'BunnyRent - Smart Housing Solutions';

function RoommateDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [roommate, setRoommate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 获取当前用户信息（实际应该根据帖子作者ID获取）
  const currentUser = getCurrentUser();
  
  useEffect(() => {
    const fetchRoommate = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await roommateAPI.getRoommatePost(slug);
        setRoommate(res.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRoommate();
  }, [slug]);

  useEffect(() => {
    if (roommate?.title) {
      document.title = `${roommate.title} | BunnyRent`;
    }
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
    };
  }, [roommate?.title]);

  if (loading) return <Container sx={{ mt: 4 }}><Typography>Loading...</Typography></Container>;
  if (error) return <Container sx={{ mt: 4 }}><Typography color="error">Failed to load roommate post.</Typography></Container>;
  if (!roommate) return null;
  
  // 发帖人信息
  const profileName = roommate.user?.name || roommate.user?.first_name || roommate.user?.email || 'User';
  const profileAvatar = roommate.user?.profile_image_url || roommate.user?.profile_image || roommate.user?.avatar || 'https://source.unsplash.com/random/400x400/?portrait';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Roommates
      </Button>

      <Grid container spacing={4}>
        {/* Left Column - Profile and Details */}
        <Grid item xs={12} md={8}>
          {/* Profile Header */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={profileAvatar}
                sx={{ width: 120, height: 120 }}
              >
                {profileName[0]}
              </Avatar>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {profileName}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {roommate.title}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Chip 
                    icon={<LocationOnIcon />} 
                    label={`${roommate.city}, ${roommate.state}`} 
                  />
                  <Chip 
                    icon={<AttachMoneyIcon />} 
                    label={`$${roommate.price}/month`} 
                    color="primary" 
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* About Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              About Me
            </Typography>
            <Typography paragraph>
              {roommate.description}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {roommate.occupation?.school && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SchoolIcon color="action" />
                    <Typography>Student at {roommate.occupation.school}</Typography>
                  </Stack>
                </Grid>
              )}
              {roommate.occupation?.workplace && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WorkIcon color="action" />
                    <Typography>Works at {roommate.occupation.workplace}</Typography>
                  </Stack>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Lifestyle Preferences
            </Typography>
            
            {/* Gender Preference */}
            {roommate.gender_preference && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Preferred Roommate Gender:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  "{roommate.gender_preference}"
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(roommate.selected_tags || []).map((tag) => (
                <Chip key={tag} label={tag} />
              ))}
            </Box>
          </Paper>

          {/* Photos Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Photos
            </Typography>
            {roommate.images && roommate.images.length > 0 && (
              <ImageGallery
                items={roommate.images.map(url => ({
                  original: url,
                  thumbnail: url,
                }))}
                showPlayButton={false}
                showFullscreenButton={true}
                showThumbnails={true}
                showNav={true}
                slideOnThumbnailOver={true}
                  />
            )}
          </Paper>

          {/* Property Details */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Property Details
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Signed: {roommate.signed ? 'Yes' : 'No'}
            </Typography>
            <List sx={{ width: '100%' }}>
              <ListItem>
                <ListItemIcon>
                  <HomeIcon color="action" />
                </ListItemIcon>
                <ListItemText primary={roommate.property_type} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocationOnIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={`${roommate.address_line1}${roommate.address_line2 ? `, ${roommate.address_line2}` : ''}`}
                  secondary={`${roommate.city}, ${roommate.state} ${roommate.zip_code}`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BedIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Total: ${roommate.total_bedrooms} Bedrooms • ${formatBathrooms(roommate.total_bathrooms)} Bathrooms`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AttachMoneyIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={`$${roommate.price} per month`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CalendarTodayIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Available from ${new Date(roommate.available_date).toLocaleDateString()}`}
                />
              </ListItem>
            </List>
          </Paper>

          {/* Roommate Requirements */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Roommates Needed
            </Typography>
                         <TableContainer>
               <Table>
                 <TableHead>
                   <TableRow>
                     <TableCell><strong>Roommate</strong></TableCell>
                     <TableCell><strong>Bedroom Type</strong></TableCell>
                     <TableCell><strong>Bathroom Type</strong></TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                  {(roommate.roommate_needs || []).map((need, index) => (
                    <TableRow key={need.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{need.roomType === 'private' ? 'Private Room' : 'Shared Room (≥2 beds)'}</TableCell>
                      <TableCell>{need.bathroomType === 'private' ? 'Private Bathroom' : 'Shared Bathroom'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right Column - Contact Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <List>
              {roommate.contact_info?.preferredContact !== 'email' && (
                <ListItem>
                  <ListItemText
                    primary="Phone"
                    secondary={roommate.contact_info?.phone}
                  />
                </ListItem>
              )}
              {roommate.contact_info?.preferredContact !== 'phone' && (
                <ListItem>
                  <ListItemText
                    primary="Email"
                    secondary={roommate.contact_info?.email}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary="Preferred Contact Method"
                  secondary={
                    roommate.contact_info?.preferredContact === 'both'
                      ? 'Both Phone and Email'
                      : roommate.contact_info?.preferredContact === 'phone'
                      ? 'Phone Only'
                      : 'Email Only'
                  }
                />
              </ListItem>
            </List>

          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default RoommateDetail; 